import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const Container = styled.main`
  min-height: 100dvh;
  max-width: 85%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;
  flex-wrap: wrap;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #013566;
`;

const DateInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  min-width: 220px;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const SecondaryButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.75rem 1.25rem;
  border: 1px solid #013566;
  border-radius: 8px;
  background-color: ${(p) => (p.$disabled ? "#f8f9fa" : "transparent")};
  color: ${(p) => (p.$disabled ? "#6c757d" : "#013566")};
  font-size: 1rem;
  font-weight: 600;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #eef3f8;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const FiltersSection = styled.div`
  width: 100%;
  margin-top: -0.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FiltersTitle = styled.div`
  font-weight: 700;
  color: #013566;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(220px, 1fr));
  gap: 0.75rem 1rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid #eee;
  background: #f8f9fa;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: #d6e2ee;
    background: #f2f6fa;
  }
`;

const PlusButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  color: #013566;
  font-weight: 800;
  line-height: 1;
  flex: 0 0 auto;

  &:hover {
    border-color: #013566;
  }
`;

const FilterGroupTitle = styled.div`
  font-weight: 700;
  color: #013566;
`;

const FilterGroupBody = styled.div`
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  color: #333;
  font-weight: 500;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0;
  cursor: pointer;
  user-select: none;
`;

const ItemText = styled.span`
  font-weight: 700;
  color: #013566;
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const TableScroller = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: scroll;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
`;

const TableInner = styled.div`
  display: inline-block;
  min-width: 100%;
  width: max-content;
  padding-right: 80px; /* ensure last column is reachable */
`;

const TableHeaderRow = styled.div`
  width: max-content;
  display: grid;
  gap: 1rem;
  align-items: center;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #eee;

  h2 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #013566;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const TableRow = styled.div`
  display: grid;
  gap: 1rem;
  align-items: center;
  border-bottom: 1px solid #eee;
  padding: 12px 16px;
  width: max-content;

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  padding: 2rem 0;
  text-align: center;
  color: #6c757d;
  font-weight: 600;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 3px solid #d6e2ee;
  border-top-color: #013566;
  animation: spin 0.9s linear infinite;
  margin: 0 auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ExpandButton = styled.button<{ $isOpen?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: ${(p) => (p.$isOpen ? "#eef3f8" : "white")};
  cursor: pointer;
  color: #013566;
  font-weight: 800;
  line-height: 1;
  flex: 0 0 auto;

  &:hover {
    border-color: #013566;
  }
`;

type Totals = {
  total_leads?: number;
  cpl_rate?: number;
  lead_cost?: number;
  traffic_price?: number;
  total_revenue?: number;
  margin?: number;
  [k: string]: any;
};

type ReportVendor = {
  vendor?: { id?: number; name?: string };
  totals?: Totals;
  campaigns?: any[];
};

type ReportClient = {
  client?: { id?: number; name?: string };
  totals?: Totals;
  vendors?: ReportVendor[];
};

const NewReportingInternal = () => {
  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  }, []);

  const sevenDaysAgoStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  }, []);

  const [startDate, setStartDate] = useState(sevenDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [showFilters, setShowFilters] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [verticals, setVerticals] = useState<any[]>([]);
  const [trafficTypes, setTrafficTypes] = useState<any[]>([]);
  const [showClients, setShowClients] = useState(true);
  const [showVendors, setShowVendors] = useState(true);
  const [showVerticals, setShowVerticals] = useState(true);
  const [showTrafficTypes, setShowTrafficTypes] = useState(true);
  const [checkedClients, setCheckedClients] = useState<Record<string, boolean>>(
    {},
  );
  const [checkedVendors, setCheckedVendors] = useState<Record<string, boolean>>(
    {},
  );
  const [checkedVerticals, setCheckedVerticals] = useState<
    Record<string, boolean>
  >({});
  const [checkedTrafficTypes, setCheckedTrafficTypes] = useState<
    Record<string, boolean>
  >({});
  const [reportData, setReportData] = useState<ReportClient[]>([]);
  const [expandedClients, setExpandedClients] = useState<
    Record<string, boolean>
  >({});
  const [reportLoading, setReportLoading] = useState(false);

  const filteredReportData = useMemo(() => {
    const isChecked = (
      map: Record<string, boolean>,
      id: string | number | undefined,
    ) => {
      if (id === undefined || id === null) return true;
      const key = String(id);
      // If the map hasn't been initialized with this id, default to visible.
      if (!(key in map)) return true;
      return !!map[key];
    };

    if (!Array.isArray(reportData) || reportData.length === 0) return [];

    return reportData
      .filter((clientBlock) =>
        isChecked(checkedClients, clientBlock?.client?.id),
      )
      .map((clientBlock) => {
        const vendorBlocks = Array.isArray(clientBlock?.vendors)
          ? clientBlock.vendors
          : [];

        const nextVendors = vendorBlocks.filter((vb) =>
          isChecked(checkedVendors, vb?.vendor?.id),
        );

        return {
          ...clientBlock,
          vendors: nextVendors,
        } satisfies ReportClient;
      });
  }, [reportData, checkedClients, checkedVendors]);

  const REPORT_COLUMNS = useMemo(
    () => [
      { key: "client", label: "Client" },
      { key: "vendor", label: "Vendor" },
      { key: "campaign_id", label: "CampaignID" },
      { key: "sub1", label: "Sub1" },
      { key: "total_leads", label: "Total Leads" },
      { key: "cpl_rate", label: "CPL Rate" },
      { key: "lead_cost", label: "Lead Cost" },
      { key: "traffic_price", label: "Traffic Price" },
      { key: "total_revenue", label: "Total Revenue" },
      { key: "margin", label: "Margin" },
    ],
    [],
  );

  const reportGridTemplate = useMemo(
    () => `repeat(${REPORT_COLUMNS.length}, 200px)`,
    [REPORT_COLUMNS.length],
  );

  const handleRunReport = async () => {
    setReportLoading(true);
    try {
      const token = localStorage.getItem("token");
      const selectedClientIds = clients
        .filter((c: any) => !!checkedClients[String(c?.id ?? c)])
        .map((c: any) => c?.id)
        .filter((id: any) => id !== undefined && id !== null)
        .map((id: any) => String(id));

      const resp = await axios.get(
        "https://api.whoffers.com/api/reports/mrg-iq-performance",
        {
          params: {
            start_date: startDate,
            end_date: endDate,
            ...(selectedClientIds.length > 0
              ? { client_ids: selectedClientIds.join(",") }
              : {}),
          },
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : undefined,
        },
      );

      console.log("Run report response:", resp.data);
      setReportData(
        Array.isArray(resp.data) ? resp.data : resp.data?.data || [],
      );
      setExpandedClients({});
    } catch (e) {
      console.error("Run report error:", e);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      const token = localStorage.getItem("token");
      const resp = await axios.get(
        "https://api.whoffers.com/api/reports/mrg-iq-performance/filters",
        token
          ? {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          : undefined,
      );
      console.log(resp.data.filters);
      const nextClients = resp.data.filters.clients || [];
      const nextVendors = resp.data.filters.vendors || [];
      const nextVerticals = resp.data.filters.verticals || [];
      const nextTrafficTypes = resp.data.filters.traffic_types || [];

      setClients(nextClients);
      setVendors(nextVendors);
      setVerticals(nextVerticals);
      setTrafficTypes(nextTrafficTypes);

      // Default everything to checked.
      const toCheckedMap = (items: any[], keyFn: (x: any) => string) => {
        const map: Record<string, boolean> = {};
        for (const item of items) map[keyFn(item)] = true;
        return map;
      };

      setCheckedClients(toCheckedMap(nextClients, (c) => String(c?.id ?? c)));
      setCheckedVendors(toCheckedMap(nextVendors, (v) => String(v?.id ?? v)));
      setCheckedVerticals(
        toCheckedMap(nextVerticals, (v) => String(v?.id ?? v)),
      );
      setCheckedTrafficTypes(toCheckedMap(nextTrafficTypes, (t) => String(t)));
    };

    run().catch((e) => console.error("Internal reporting filters error:", e));
  }, []);

  return (
    <Container>
      <PageTitle>Internal Reporting</PageTitle>

      <ControlsBar>
        <Field>
          <Label htmlFor="internal-start-date">Start date</Label>
          <DateInput
            id="internal-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field>
          <Label htmlFor="internal-end-date">End date</Label>
          <DateInput
            id="internal-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>

        <ActionsGroup style={{ marginLeft: "auto" }}>
          <SecondaryButton
            type="button"
            onClick={() => setShowFilters((v) => !v)}
          >
            Filters
          </SecondaryButton>
          <SecondaryButton type="button" onClick={handleRunReport}>
            Run Report
          </SecondaryButton>
        </ActionsGroup>
      </ControlsBar>

      {showFilters && (
        <FiltersSection>
          <FiltersHeader>
            <FiltersTitle>Show / Hide columns</FiltersTitle>
            <ActionsGroup>
              <SecondaryButton
                type="button"
                onClick={() => {
                  const checkAll = (
                    items: any[],
                    keyFn: (x: any) => string,
                  ) => {
                    const map: Record<string, boolean> = {};
                    for (const item of items) map[keyFn(item)] = true;
                    return map;
                  };
                  setCheckedClients(
                    checkAll(clients, (c) => String(c?.id ?? c)),
                  );
                  setCheckedVendors(
                    checkAll(vendors, (v) => String(v?.id ?? v)),
                  );
                  setCheckedVerticals(
                    checkAll(verticals, (v) => String(v?.id ?? v)),
                  );
                  setCheckedTrafficTypes(
                    checkAll(trafficTypes, (t) => String(t)),
                  );
                }}
              >
                Show All
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => {
                  const uncheckAll = (
                    items: any[],
                    keyFn: (x: any) => string,
                  ) => {
                    const map: Record<string, boolean> = {};
                    for (const item of items) map[keyFn(item)] = false;
                    return map;
                  };
                  setCheckedClients(
                    uncheckAll(clients, (c) => String(c?.id ?? c)),
                  );
                  setCheckedVendors(
                    uncheckAll(vendors, (v) => String(v?.id ?? v)),
                  );
                  setCheckedVerticals(
                    uncheckAll(verticals, (v) => String(v?.id ?? v)),
                  );
                  setCheckedTrafficTypes(
                    uncheckAll(trafficTypes, (t) => String(t)),
                  );
                }}
              >
                Hide All
              </SecondaryButton>
            </ActionsGroup>
          </FiltersHeader>

          <FiltersGrid>
            <div>
              <FilterGroupHeader
                type="button"
                onClick={() => setShowClients((v) => !v)}
              >
                <PlusButton
                  type="button"
                  aria-label="Toggle Clients"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowClients((v) => !v);
                  }}
                >
                  {showClients ? "−" : "+"}
                </PlusButton>
                <FilterGroupTitle>Clients</FilterGroupTitle>
              </FilterGroupHeader>
              {showClients && (
                <FilterGroupBody>
                  <ItemsList>
                    {clients.length === 0 ? (
                      <div style={{ color: "#6c757d", fontWeight: 600 }}>
                        No clients
                      </div>
                    ) : (
                      clients.map((c: any, idx: number) => (
                        <ItemRow key={c?.id ?? `${c}-${idx}`}>
                          <input
                            type="checkbox"
                            checked={!!checkedClients[String(c?.id ?? c)]}
                            onChange={() => {
                              const key = String(c?.id ?? c);
                              setCheckedClients((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          />
                          <ItemText>{c?.name ?? String(c)}</ItemText>
                        </ItemRow>
                      ))
                    )}
                  </ItemsList>
                </FilterGroupBody>
              )}
            </div>

            <div>
              <FilterGroupHeader
                type="button"
                onClick={() => setShowVendors((v) => !v)}
              >
                <PlusButton
                  type="button"
                  aria-label="Toggle Vendors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowVendors((v) => !v);
                  }}
                >
                  {showVendors ? "−" : "+"}
                </PlusButton>
                <FilterGroupTitle>Vendors</FilterGroupTitle>
              </FilterGroupHeader>
              {showVendors && (
                <FilterGroupBody>
                  <ItemsList>
                    {vendors.length === 0 ? (
                      <div style={{ color: "#6c757d", fontWeight: 600 }}>
                        No vendors
                      </div>
                    ) : (
                      vendors.map((v: any, idx: number) => (
                        <ItemRow key={v?.id ?? `${v}-${idx}`}>
                          <input
                            type="checkbox"
                            checked={!!checkedVendors[String(v?.id ?? v)]}
                            onChange={() => {
                              const key = String(v?.id ?? v);
                              setCheckedVendors((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          />
                          <ItemText>{v?.name ?? String(v)}</ItemText>
                        </ItemRow>
                      ))
                    )}
                  </ItemsList>
                </FilterGroupBody>
              )}
            </div>

            <div>
              <FilterGroupHeader
                type="button"
                onClick={() => setShowVerticals((v) => !v)}
              >
                <PlusButton
                  type="button"
                  aria-label="Toggle Verticals"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowVerticals((v) => !v);
                  }}
                >
                  {showVerticals ? "−" : "+"}
                </PlusButton>
                <FilterGroupTitle>Vertical</FilterGroupTitle>
              </FilterGroupHeader>
              {showVerticals && (
                <FilterGroupBody>
                  <ItemsList>
                    {verticals.length === 0 ? (
                      <div style={{ color: "#6c757d", fontWeight: 600 }}>
                        No verticals
                      </div>
                    ) : (
                      verticals.map((v: any, idx: number) => (
                        <ItemRow key={v?.id ?? `${v}-${idx}`}>
                          <input
                            type="checkbox"
                            checked={!!checkedVerticals[String(v?.id ?? v)]}
                            onChange={() => {
                              const key = String(v?.id ?? v);
                              setCheckedVerticals((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          />
                          <ItemText>{v?.name ?? String(v)}</ItemText>
                        </ItemRow>
                      ))
                    )}
                  </ItemsList>
                </FilterGroupBody>
              )}
            </div>

            <div>
              <FilterGroupHeader
                type="button"
                onClick={() => setShowTrafficTypes((v) => !v)}
              >
                <PlusButton
                  type="button"
                  aria-label="Toggle Traffic Types"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTrafficTypes((v) => !v);
                  }}
                >
                  {showTrafficTypes ? "−" : "+"}
                </PlusButton>
                <FilterGroupTitle>Traffic Type</FilterGroupTitle>
              </FilterGroupHeader>
              {showTrafficTypes && (
                <FilterGroupBody>
                  <ItemsList>
                    {trafficTypes.length === 0 ? (
                      <div style={{ color: "#6c757d", fontWeight: 600 }}>
                        No traffic types
                      </div>
                    ) : (
                      trafficTypes.map((t: any, idx: number) => (
                        <ItemRow key={`${t}-${idx}`}>
                          <input
                            type="checkbox"
                            checked={!!checkedTrafficTypes[String(t)]}
                            onChange={() => {
                              const key = String(t);
                              setCheckedTrafficTypes((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }}
                          />
                          <ItemText>{String(t)}</ItemText>
                        </ItemRow>
                      ))
                    )}
                  </ItemsList>
                </FilterGroupBody>
              )}
            </div>
          </FiltersGrid>
        </FiltersSection>
      )}

      <Tile>
        <TableScroller>
          <TableInner>
            <TableHeaderRow style={{ gridTemplateColumns: reportGridTemplate }}>
              {REPORT_COLUMNS.map((c) => (
                <h2 key={c.key}>{c.label}</h2>
              ))}
            </TableHeaderRow>

            <TableBody>
              {reportLoading ? (
                <div style={{ padding: "2rem 0" }}>
                  <Spinner />
                </div>
              ) : filteredReportData.length === 0 ? (
                <EmptyState>No rows yet</EmptyState>
              ) : (
                filteredReportData.flatMap((clientBlock, clientIdx) => {
                  const clientId = String(clientBlock?.client?.id ?? clientIdx);
                  const isOpen = !!expandedClients[clientId];
                  const clientTotals = clientBlock?.totals || {};
                  const vendorBlocks = Array.isArray(clientBlock?.vendors)
                    ? clientBlock.vendors
                    : [];

                  const clientRow = (
                    <TableRow
                      key={`client-${clientId}`}
                      style={{
                        gridTemplateColumns: reportGridTemplate,
                        background: "#f8f9fa",
                        borderRadius: 8,
                      }}
                    >
                      {REPORT_COLUMNS.map((c) => {
                        if (c.key === "client") {
                          return (
                            <Cell
                              key={c.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                fontWeight: 800,
                                color: "#013566",
                              }}
                            >
                              <ExpandButton
                                type="button"
                                aria-label={
                                  isOpen ? "Collapse client" : "Expand client"
                                }
                                $isOpen={isOpen}
                                onClick={() =>
                                  setExpandedClients((prev) => ({
                                    ...prev,
                                    [clientId]: !prev[clientId],
                                  }))
                                }
                              >
                                {isOpen ? "−" : "+"}
                              </ExpandButton>
                              <span>{clientBlock?.client?.name ?? "N/A"}</span>
                            </Cell>
                          );
                        }
                        if (c.key === "vendor") return <Cell key={c.key} />;
                        if (c.key === "campaign_id")
                          return <Cell key={c.key} />;
                        if (c.key === "sub1") return <Cell key={c.key} />;
                        const val = clientTotals?.[c.key];
                        return <Cell key={c.key}>{val ?? "N/A"}</Cell>;
                      })}
                    </TableRow>
                  );

                  const vendorRows =
                    isOpen && vendorBlocks.length > 0
                      ? vendorBlocks.map((vb, vIdx) => {
                          const vendorTotals = vb?.totals || {};
                          const vendorKey = String(
                            vb?.vendor?.id ?? `${clientId}-${vIdx}`,
                          );
                          return (
                            <TableRow
                              key={`vendor-${clientId}-${vendorKey}`}
                              style={{
                                gridTemplateColumns: reportGridTemplate,
                              }}
                            >
                              {REPORT_COLUMNS.map((c) => {
                                if (c.key === "client") {
                                  return <Cell key={c.key} />;
                                }
                                if (c.key === "vendor") {
                                  return (
                                    <Cell
                                      key={c.key}
                                      style={{
                                        fontWeight: 800,
                                        paddingLeft: 12,
                                      }}
                                    >
                                      {vb?.vendor?.name ?? "N/A"}
                                    </Cell>
                                  );
                                }
                                if (c.key === "campaign_id")
                                  return <Cell key={c.key} />;
                                if (c.key === "sub1")
                                  return <Cell key={c.key} />;
                                const val = vendorTotals?.[c.key];
                                return <Cell key={c.key}>{val ?? "N/A"}</Cell>;
                              })}
                            </TableRow>
                          );
                        })
                      : [];

                  return [clientRow, ...vendorRows];
                })
              )}
            </TableBody>
          </TableInner>
        </TableScroller>
      </Tile>
    </Container>
  );
};

export default NewReportingInternal;
